"use client"

import { autoUpdate, flip, offset, shift, useFloating } from "@floating-ui/react-dom"
import { useInViewport } from "ahooks"
import { ReactNode, useRef, useState } from "react"
import { ExperimentVariantSwitcher } from "src/components/debug/ExperimentVariantSwitcher"
import { ExperimentKey, ExperimentVariant } from "src/integrations/posthog/experiments"
import { useExperimentVariant } from "src/integrations/posthog/useExperiment"

const isDev = process.env.NODE_ENV === "development"

export type ExperimentProps<Key extends ExperimentKey> = {
  experiment: Key
  components: Record<ExperimentVariant<Key>, ReactNode>
  loadingFallback?: ReactNode
  disableWrapper?: boolean
}

export function Experiment<Key extends ExperimentKey>({
  experiment,
  components,
  loadingFallback,
  disableWrapper = false,
}: ExperimentProps<Key>) {
  const { loading, variant } = useExperimentVariant(experiment)
  const anchorRef = useRef<HTMLDivElement>(null)
  const [inView] = useInViewport(anchorRef)
  const [closed, setClosed] = useState(false)

  const { refs, floatingStyles } = useFloating({
    elements: { reference: anchorRef.current },
    middleware: [offset(16), flip(), shift()],
    whileElementsMounted: autoUpdate,
    placement: "top-start",
  })

  const children = loading && loadingFallback ? <>{loadingFallback}</> : components[variant] || null

  if (disableWrapper || closed) {
    return children
  }

  return (
    <div ref={anchorRef} className="w-full">
      {isDev && inView && (
        <div
          ref={refs.setFloating}
          style={floatingStyles}
          className="z-tooltip cursor-default"
          onKeyDown={undefined}
          onClick={(event) => {
            event.stopPropagation()
            event.preventDefault()
          }}
        >
          <ExperimentVariantSwitcher experiment={experiment} onClose={() => setClosed(true)} />
        </div>
      )}

      {children}
    </div>
  )
}
